import { AxiosResponse } from 'axios'
import { Fetcher } from 'swr'
import {
  ICompanyStats,
  ICompanyData,
  IBatchScans,
  ICompanyDashboardData,
  ICompanyDashboardDataV2,
  ICompanyScoreTrend,
  ICompanyBatchScanData,
  IOnboardingStatus,
  ThreatLevel,
  ICompanyAvailableSeatsInputDto,
  ICompanyAvailableSeatsOutputDto,
  IBulkScansListParams,
  IBulkScansPaginatedResponse,
} from 'models/company.model'
import {
  ConfirmBatchUploadRequest,
  ConfirmBatchUploadResponse,
  BatchPreviewResponse,
  ConfirmBatchScanResponse,
  SkipScanResponse,
} from 'models/directory.model'
import {
  EUserInvited,
  IUserDetailed,
  TCognitoUserAdmin,
  GetAllUsersForBulkNotifyOutputDto,
  GetAllUsersForBulkNotifyInputDto,
  GetAllUsersForBulkNotifyInitialOutputDto,
  GetAllUsersCountForBulkNotifyOutputDto,
  GetAllUsersCountForBulkNotifyInputDto,
  DeepScanStatus,
  LeakCategory,
  IUserRow,
} from 'models/user.model'
import { PaginatedData, PaginationMetadata } from 'models/pagination.model'
import api from './api'
import { LeakType } from 'models/leak.model'
import { resolveActiveCompanyId } from 'util/superAdmin'
import { SortOrder } from 'models/sort.model'
import { RiskLevel } from 'constants/risk-level'

export { RiskLevel }

export const DefaultValuesUserDetails: IUserDetailed = {
  mfa: false,
  useMFA: false,
  fullname: '',
  role: null,
  caption: '',
  cybersecurityResponsePlan: false,
  haveResponsePlan: false,
  havePasswordManager: false,
  training: '',
  email: '',
  createdAt: null,
  companyScanLastDate: null,
  phoneNumber: '',
  location: '',
  deepScanStatus: DeepScanStatus.NOT_STARTED,
  isScanning: false,
  userProtexxaScore: 0,
  protexxaScoreDelta: null,
  recommendations: [],
  deepScan: {
    lastScan: '',
    nextScheduledScan: '',
    totalExposures: 0,
    takedownRequests: 0,
    passwordCompromises: 0,
    emailExposures: 0,
    addressExposures: 0,
    nameExposures: 0,
    imageExposures: 0,
  },
  exposuresTable: [],
  findingsData: {
    total: 0,
    findings: [],
  },
}

const isTokenValid = (token: string): Promise<AxiosResponse> => {
  return api.get(`/v1/invites/isValid/${token}`)
}

const verifyUserCompanySignUp = (email: string, company: string): Promise<AxiosResponse> => {
  return api.post('/v1/invites/validate-invite', { email: email.toLowerCase(), company })
}

const verifyCompanySeats = (companyId: string): Promise<AxiosResponse> => {
  return api.get(`/v1/company/checkSeats/${companyId}`)
}

const companyFetcher: Fetcher<ICompanyData, string> = (companyId: string) =>
  api.get(`/v2/company/${companyId}`).then(response => response.data)

const getCompanyV2 = async (): Promise<ICompanyData> => {
  const companyId = resolveActiveCompanyId()

  const { data } = await api.get<ICompanyData>(`/v2/company/${companyId}`)
  return data
}

const upsertOnboardingLock = async (companyId: string): Promise<void> => {
  return api.post(`/v2/company/${companyId}/onboarding/lock`)
}

const getCompanyUsers = async (
  page: number,
  role: string,
  status: string,
): Promise<{ users: EUserInvited[]; totalPages: number | string }> => {
  let query = ''
  if (role) query = query.concat(`&role=${role}`)
  if (status) query = query.concat(`&status=${status}`)
  const { data, headers } = await api.get<EUserInvited[]>(`/v1/user/company?page=${page}${query}`)
  return { users: data, totalPages: headers['x-total-pages'] }
}

const putCompany = async (changedData: Partial<ICompanyData>): Promise<ICompanyData> => {
  const { data } = await api.put<ICompanyData>('/v1/company', changedData)
  return data
}

const getCompanyStatsV2 = async (): Promise<ICompanyStats> => {
  const companyId = resolveActiveCompanyId()

  const { data } = await api.get<ICompanyStats>(`/v2/company/${companyId}/stats`)
  return data
}

const getLinkInvite = async (): Promise<string> => {
  const { data } = await api.get<string>('/v1/company/invite')
  return data
}

const deleteAdmin = async (email: string): Promise<AxiosResponse> => {
  const { data } = await api.delete(`/v1/user/${email}`)
  return data
}

const updateUserRole = async (role: string, cognitoId: string): Promise<AxiosResponse> => {
  const { data } = await api.put(`/v1/user/role/${cognitoId}`, { role: role })
  return data
}

const updateInviteRole = async (role: string, email: string): Promise<AxiosResponse> => {
  const { data } = await api.put(`/v1/invites/${email}`, { role: role })
  return data
}

const resendUserInvite = async (uid: string) => {
  return api.post(`/v1/invites/resend-only/${uid}`)
}

const resendAllInvites = async () => {
  return api.post(`/v1/invites/resend`)
}

const checkInvite = async (inviteCode: string): Promise<AxiosResponse> => {
  const { data } = await api.get<AxiosResponse>(`/v1/company/checkInvite/${inviteCode}`)
  return data
}

const getCompanyAdmins = async (): Promise<TCognitoUserAdmin[]> => {
  const { data } = await api.get<TCognitoUserAdmin[]>('/v1/user/company')
  return data
}

export interface UploadBatchFileOptions {
  compareMode?: boolean
  onProgress?: (progress: number) => void
}

const uploadBatchFile = async (
  companyId: string,
  file: File,
  options?: UploadBatchFileOptions,
): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const url = options?.compareMode
    ? `/v2/company/${companyId}/scan/batch/upload?compareMode=true`
    : `/v2/company/${companyId}/scan/batch/upload`

  return api.post(url, formData, {
    onUploadProgress: progressEvent => {
      if (options?.onProgress && progressEvent.total) {
        options.onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100))
      }
    },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const completeOnboarding = async (
  companyId: string,
  params: { industry: string; companyName: string },
): Promise<AxiosResponse> => {
  return api.post(`/v2/company/${companyId}/onboarding`, params, {})
}

const startBatchScan = async (companyId: string): Promise<AxiosResponse> => {
  return api.post(`/v2/company/${companyId}/scan/batch/start`)
}

const getBatchProgress = async (companyId: string): Promise<AxiosResponse> => {
  return api.get(`/v2/company/${companyId}/scan/batch/progress`)
}

const getBatchScans = async (companyId: string): Promise<PaginatedData<IBatchScans> | void> => {
  const { data } = await api.get(`/v2/company/${companyId}/batch`)
  //remove when and update when backend sends pagination
  const paginationMock = {
    currentPage: 1,
    totalRecords: 12,
    totalPages: 1,
    recordsPerPage: 10,
  }

  return { data, pagination: paginationMock }
}

const downloadBatchFile = async (companyId: string): Promise<void> => {
  try {
    const response: AxiosResponse<Blob> = await api.get(
      `/v2/company/${companyId}/scan/batch/download`,
      {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      },
    )

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    link.setAttribute('download', 'bulk-scan-report.xlsx')
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error: unknown) {
    console.error('Error downloading the file:', error)
    throw error
  }
}

const downloadBatchFileById = async (
  companyId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  batchScanMetadataId: string,
): Promise<void> => {
  return downloadBatchFile(companyId)
}

const getCompanyDashboard = async (companyId: string): Promise<ICompanyDashboardData> => {
  const { data } = await api.get(`/v2/company/${companyId}/dashboard`)
  return data
}

export interface IDepartmentDetails {
  name: string
  users: number
  hasCyberSecurityPlan: number
  hasMfa: number
  hasTraining: number
  deepScanStatus: { [key in DeepScanStatus]: number }
  averageScore: number
  trend: { score: number; date: string }[]
}

export interface IDepartmentUserData {
  name: string
  users: number
  vulnerabilities: string[]
  riskLevel: ThreatLevel
}

export type IDepartmentUserResponse = PaginatedData<IDepartmentUserData>

const fetchDepartmentUsersData = async (
  companyId: string,
  params: {
    page?: number
    perPage?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    department?: string
    riskLevel?: string
    vulnerabilities?: string
    search?: string
  },
): Promise<IDepartmentUserResponse> => {
  const data = await api.get(`/v2/company/${companyId}/department`, { params })
  return data.data
}

const fetchDepartmentById = async (
  companyId: string,
  departmentId: string,
): Promise<IDepartmentDetails> => {
  try {
    const { data } = await api.get<IDepartmentDetails>(
      `/v2/company/${companyId}/department/${departmentId}`,
    )
    return data
  } catch (error) {
    console.error('Error fetching department details:', error)
    throw error
  }
}

// export const searchDepartmentUsers = async (): Promise<IDepartmentUserData[]> => { }

export enum SortField {
  EMAIL = 'email',
  NAME = 'fullname',
  RISK_LEVEL = 'riskLevel',
  DEEP_SCAN_STATUS = 'deepScanStatus',
  FINDINGS_COUNT = 'findingsCount',
}

type GetCompanyAllUserDataQuery = {
  // pagination
  page?: number
  perPage?: number
  // sorting
  sortBy?: SortField
  sortOrder?: SortOrder
  // filters
  search?: string
  deepScanStatus?: DeepScanStatus
  isActive?: boolean
  riskLevel?: string
  queuedOnly?: boolean
  deactivatedBy?: string
  deactivatedAtFrom?: string
  deactivatedAtTo?: string
}

const companyUsersFetcher = async ([companyId, params]: [
  string,
  GetCompanyAllUserDataQuery,
]): Promise<PaginatedData<IUserRow>> =>
  api.get(`/v2/company/${companyId}/user`, { params }).then(res => res.data)

const companyDepartmentsFetcher = async ([companyId, params]: [
  string,
  GetCompanyAllUserDataQuery,
]): Promise<
  PaginatedData<{
    id: string
    name: string
    userCount: number
    riskLevels: { riskLevel: RiskLevel }[]
    users: {
      id: string
      name: string
      email: string
    }[]
  }>
> => api.get(`/v2/company/${companyId}/department`, { params }).then(res => res.data)

export type CompanyUsersStats = {
  activeUsers: number
  inactiveUsers: number
  queuedUsers: number
}

const companyUsersStatsFetcher: Fetcher<CompanyUsersStats, string> = (companyId: string) =>
  api.get(`/v2/company/${companyId}/user/stats`).then(res => res.data)

const getCompanyUserByRegister = async (
  companyId: string,
  registerId: string,
  startDate: Date | null,
  endDate: Date | null,
  leakCategory: LeakCategory,
): Promise<IUserDetailed> => {
  const { data }: AxiosResponse<IUserDetailed> = await api.get(
    `/v2/company/${companyId}/user/${registerId}`,
    {
      params: {
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
        leakCategory,
      },
    },
  )
  return data
}

export interface IFindings {
  id: string
  fullname: string
  email?: string
  caption: string
  source: string
  issue: string
  threatLevel: 'Low' | 'Medium' | 'High' | 'Critical'
  riskLevel: RiskLevel | null
  defaultRiskLevel: RiskLevel | null
  dateOfExposure: Date | string
  dateBreached: Date | string
  deepScanStatus?: DeepScanStatus
  value: string
}

export enum GetUsersFindingsSortField {
  FULLNAME = 'fullname',
  SOURCE = 'source',
  ISSUE = 'issue',
  THREAT_LEVEL = 'threatLevel',
  DATE_OF_EXPOSURE = 'dateOfExposure',
}

export type UsersFindingsLeakType =
  | LeakType.NAME
  | LeakType.EMAIL
  | LeakType.CREDENTIAL
  | LeakType.PASSWORD_LEAK
  | LeakType.MENTION
  | LeakType.BOTNET
  | LeakType.PLAIN_TEXT_PASSWORD

export interface BaseFindingsQueryParams {
  source?: string
  sourceUrl?: string
  leakType?: UsersFindingsLeakType
  startDate?: string | Date
  endDate?: string | Date
  sortBy?: GetUsersFindingsSortField
  sortOrder?: 'asc' | 'desc'
}

export interface CompanyFindingsQueryParams extends BaseFindingsQueryParams {
  source: string
  fullname?: string
  departmentId?: string
  threatLevel?: string
  webDomain?: string
  page?: number
  perPage?: number
}

export interface UserFindingsByRegisterQueryParams extends BaseFindingsQueryParams {
  riskLevel?: string
  page?: number
  perPage?: number
}

export interface FindingsResponse {
  findings: IFindings[]
  pagination: PaginationMetadata
}

const getCompanyFindings = async (
  companyId: string,
  params: CompanyFindingsQueryParams,
): Promise<FindingsResponse> => {
  const { data } = await api.get(`/v2/company/${companyId}/user/findings`, {
    params,
  })

  return data
}

const getCompanyUserFindingsByRegister = async (
  companyId: string,
  registerId: string,
  params: UserFindingsByRegisterQueryParams,
): Promise<FindingsResponse> => {
  const { data } = await api.get(`/v2/company/${companyId}/user/${registerId}/findings`, {
    params,
  })

  return data
}

const getCompanyFindingsSummary = async (
  companyId: string,
  params: {
    webDomain?: string
    source?: string
  },
): Promise<{
  summary: {
    passwordLeaks: number
    namesExposed: number
    emailsExposed: number
    mentions?: number
    totalExposures: number
    percentageOfTotal: number
  }
  webDomains: string[]
  sources: string[]
  departments: { _id: string; name: string }[]
}> => {
  const { data } = await api.get(`/v2/company/${companyId}/user/findings/summary`, {
    params,
  })

  return data
}

const updateCompanyFindingRiskLevel = async (
  companyId: string,
  leakId: string,
  riskLevel: RiskLevel,
): Promise<{
  success: boolean
  data: {
    leakId: string
    riskLevel: RiskLevel
  }
}> => {
  const { data } = await api.patch(`/v2/company/${companyId}/user/findings/${leakId}/risk-level`, {
    riskLevel,
  })

  return data
}

const getAllUsersForBulkInvite = async ({
  companyId,
  search,
  filter,
}: GetAllUsersForBulkNotifyInputDto): Promise<GetAllUsersForBulkNotifyOutputDto[]> => {
  const { data } = await api.get<GetAllUsersForBulkNotifyOutputDto[]>(
    `/v2/company/${companyId}/invites/notify`,
    {
      params: { search, filter },
    },
  )
  const formattedData = data.map((instance: GetAllUsersForBulkNotifyInitialOutputDto) => ({
    ...instance,
    selected: false,
  }))
  return formattedData
}

const getAllUsersCountForBulkInvite = async ({
  companyId,
  filter,
}: GetAllUsersCountForBulkNotifyInputDto): Promise<GetAllUsersCountForBulkNotifyOutputDto> => {
  const { data } = await api.get<GetAllUsersCountForBulkNotifyOutputDto>(
    `/v2/company/${companyId}/invites/notify/count`,
    {
      params: { filter },
    },
  )

  return data
}

const companyDashboardFetcher: Fetcher<ICompanyDashboardDataV2, [string, string]> = ([companyId]: [
  string,
  string,
]) => api.get(`/v2/company/${companyId}/dashboard/v2`).then(response => response.data)

const getCompanyScoreTrend = async (
  companyId: string,
  months: number,
): Promise<ICompanyScoreTrend> => {
  const { data } = await api.get(`/v2/company/${companyId}/score/trend?months=${months}`)
  const result = {
    trend: data.trend.map(item => ({
      ...item,
      date: new Date(item.date),
    })),
  }
  return result
}

const getBatchScanStatus = async (companyId: string): Promise<ICompanyBatchScanData> => {
  const { data } = await api.get(`/v2/company/${companyId}/scan/batch/progress`)
  return data
}

export type BatchScanMetadataStatusResponse = {
  id: string
  status: string
}

const getBatchScanMetadataStatus = async (
  companyId: string,
  batchScanMetadataId: string,
): Promise<BatchScanMetadataStatusResponse> => {
  const { data } = await api.get(
    `/v2/company/${companyId}/scan/batch/${batchScanMetadataId}/status`,
  )
  return data
}

const getOnboardingStatus = async (companyId: string): Promise<IOnboardingStatus> => {
  const { data } = await api.get(`/v2/company/${companyId}/status`)
  return data
}

const getCompanySeats = async ({
  companyId,
  seats,
}: ICompanyAvailableSeatsInputDto): Promise<ICompanyAvailableSeatsOutputDto> => {
  const { data } = await api.get(`/v2/company/${companyId}/scan/batch/seats`, {
    params: { seats },
  })
  return data
}

const getCompanyScansMetadata = async (companyId: string): Promise<IBulkScansPaginatedResponse> => {
  const { data } = await api.get<IBulkScansPaginatedResponse>(`/v2/company/${companyId}/scan/batch`)
  return data
}

const getCompanyScansPaginated = async (
  companyId: string,
  params: IBulkScansListParams,
): Promise<IBulkScansPaginatedResponse> => {
  const { data } = await api.get<IBulkScansPaginatedResponse>(
    `/v2/company/${companyId}/scan/batch`,
    { params },
  )
  return data
}

const inviteMultipleUsersDeepScan = async (
  companyId: string,
  registerIds: string[],
): Promise<AxiosResponse> => {
  return api.post(`/v2/company/${companyId}/invites/invite`, { registerIds })
}

export type BatchDeactivateUsersOutputDto = {
  totalProcessed: number
  successfullyDeactivated: number
  skipped: number
  failed: number
  details: {
    deactivated: { registerId: string }[]
    skipped: { registerId: string; reason?: string }[]
    failed: { registerId: string; error: string }[]
  }
}

const batchDeactivateUsers = async (
  companyId: string,
  userIds: string[],
): Promise<BatchDeactivateUsersOutputDto> => {
  const { data } = await api.post<BatchDeactivateUsersOutputDto>(
    `/v2/company/${companyId}/user/batch-deactivate`,
    { userIds },
  )

  return data
}

export type BatchReactivateUsersOutputDto = BatchDeactivateUsersOutputDto

const batchReactivateUsers = async (
  companyId: string,
  userIds: string[],
): Promise<BatchReactivateUsersOutputDto> => {
  const { data } = await api.post<BatchReactivateUsersOutputDto>(
    `/v2/company/${companyId}/user/batch-activate`,
    { userIds },
  )

  return data
}

export type BatchDeleteUsersOutputDto = BatchDeactivateUsersOutputDto

const batchDeleteUsers = async (
  companyId: string,
  userIds: string[],
): Promise<BatchDeleteUsersOutputDto> => {
  const { data } = await api.delete<BatchDeleteUsersOutputDto>(
    `/v2/company/${companyId}/user/batch-delete`,
    { data: { userIds } },
  )

  return data
}

const fetchBatchFileBlob = async (
  companyId: string,
  batchScanMetadataId: string,
): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await api.get(
    `/v2/company/${companyId}/scan/batch/${batchScanMetadataId}/populate`,
    {
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
    },
  )
  return response.data
}

const confirmBatchUpload = async (
  companyId: string,
  params: ConfirmBatchUploadRequest,
): Promise<ConfirmBatchUploadResponse> => {
  const { data } = await api.post<ConfirmBatchUploadResponse>(
    `/v2/company/${companyId}/scan/batch/upload/confirm`,
    params,
  )
  return data
}

const previewBatchUpload = async (companyId: string, file: File): Promise<BatchPreviewResponse> => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post<BatchPreviewResponse>(
    `/v2/company/${companyId}/scan/batch/preview`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

const confirmBatchScan = async (
  companyId: string,
  previewToken: string,
): Promise<ConfirmBatchScanResponse> => {
  const { data } = await api.post<ConfirmBatchScanResponse>(
    `/v2/company/${companyId}/scan/batch/confirm`,
    { previewToken },
  )
  return data
}

const skipScanUpdateList = async (
  companyId: string,
  previewToken: string,
): Promise<SkipScanResponse> => {
  const { data } = await api.post<SkipScanResponse>(
    `/v2/company/${companyId}/scan/batch/skip-scan`,
    { previewToken },
  )
  return data
}

export type ActiveUsersRiskLevelDistribution = {
  riskLevels: {
    [key in RiskLevel]: {
      count: number
      percentage: number
    }
  }
  userCount: number
}

const companyActiveUsersRiskLevelDistributionFetcher: Fetcher<
  ActiveUsersRiskLevelDistribution,
  [string, string]
> = ([companyId]: [string, string]) =>
  api
    .get(`/v2/company/${companyId}/stats/active-users-risk-level-distribution`)
    .then(response => response.data)

export default {
  isTokenValid,
  deleteAdmin,
  verifyCompanySeats,
  getCompanyUsers,
  verifyUserCompanySignUp,
  getCompanyV2,
  putCompany,
  getCompanyStatsV2,
  getLinkInvite,
  checkInvite,
  updateUserRole,
  updateInviteRole,
  resendUserInvite,
  resendAllInvites,
  getCompanyAdmins,
  uploadBatchFile,
  getBatchProgress,
  getBatchScans,
  downloadBatchFile,
  downloadBatchFileById,
  getCompanyDashboard,
  getCompanyScoreTrend,
  fetchDepartmentUsersData,
  fetchDepartmentById,
  getBatchScanStatus,
  getBatchScanMetadataStatus,
  getCompanyUserByRegister,
  getCompanyUserFindingsByRegister,
  getCompanyFindings,
  getCompanyFindingsSummary,
  updateCompanyFindingRiskLevel,
  getAllUsersForBulkInvite,
  completeOnboarding,
  getOnboardingStatus,
  getCompanySeats,
  getCompanyScansMetadata,
  getCompanyScansPaginated,
  startBatchScan,
  getAllUsersCountForBulkInvite,
  inviteMultipleUsersDeepScan,
  batchDeactivateUsers,
  batchReactivateUsers,
  batchDeleteUsers,
  upsertOnboardingLock,
  fetchBatchFileBlob,
  confirmBatchUpload,
  companyFetcher,
  previewBatchUpload,
  confirmBatchScan,
  skipScanUpdateList,
  companyUsersStatsFetcher,
  companyUsersFetcher,
  companyDepartmentsFetcher,
  companyDashboardFetcher,
  companyActiveUsersRiskLevelDistributionFetcher,
}
